import {
  AdvicePriceFormValues,
  OneTimePaymentAdvisorChoice,
} from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/interfaces";
import {
  CriticalIllnessCoverage,
  DeathCoverage,
  DisabilityCoverage,
} from "Store/interfaces/products/coverage";

import { AdviceProduct } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import { formatNumberStringToNumber } from "Formatting/index";
import { AddProduct } from "Store/addProductToCart/interfaces";
import { AdvicePriceCalculationResult } from "Store/advice/interfaces";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { isDefined } from "Utils/functions";

export const mapAdvicePriceCalculationResultToAddProduct = (
  priceCalculations: AdvicePriceCalculationResult[],
  adviceProductsOnCart: AdviceProduct[],
  formProps: AdvicePriceFormValues,
  isAdvisor: boolean,
): AddProduct[] =>
  priceCalculations
    .filter((calculation) => {
      const configName = calculation.configurationName;
      const {
        disabilityCheckedToPurchase,
        deathCheckedToPurchase,
        criticalIllnessCheckedToPurchase,
      } = formProps;
      return (
        (configName === DisabilityCoverage.DISABILITY_FLEX &&
          disabilityCheckedToPurchase) ||
        (configName === DeathCoverage.DEATH_NOT_SMOKER &&
          deathCheckedToPurchase) ||
        (configName === CriticalIllnessCoverage.CRITICAL_ILLNESS_NOT_SMOKER &&
          criticalIllnessCheckedToPurchase)
      );
    })
    .map((calculation) => {
      const configName = calculation.configurationName;
      switch (configName) {
        case DisabilityCoverage.DISABILITY_FLEX:
          return mapAdviceProductToAddProduct(
            INSURANCE_NAME_VALUE.DISABILITY_FLEX,
            calculation,
            adviceProductsOnCart,
            formProps.disabilityInsuredSumValue,
            formProps,
            isAdvisor,
          );
        case DeathCoverage.DEATH_NOT_SMOKER:
          return mapAdviceProductToAddProduct(
            INSURANCE_NAME_VALUE.DEATH,
            calculation,
            adviceProductsOnCart,
            formProps.deathInsuredSumValue,
          );
        case CriticalIllnessCoverage.CRITICAL_ILLNESS_NOT_SMOKER:
          return mapAdviceProductToAddProduct(
            INSURANCE_NAME_VALUE.CRITICAL_ILLNESS,
            calculation,
            adviceProductsOnCart,
            formProps.criticalIllnessInsuredSumValue,
          );
        default:
          throw new Error("Unable to map mapAdviceProduct to addProduct");
      }
    });

const mapAdviceProductToAddProduct = (
  insuranceName: INSURANCE_NAME_VALUE,
  calculation: AdvicePriceCalculationResult,
  adviceProductsOnCart: AdviceProduct[],
  insuredSum: number,
  formProps?: AdvicePriceFormValues,
  isAdvisor?: boolean,
): AddProduct => {
  const productId = getAdviceProductIdIfExists(
    insuranceName,
    adviceProductsOnCart,
  );
  const addProduct: AddProduct = {
    type: insuranceName,
    coverage: calculation.configurationName,
    priceData: calculation.prices[0],
    insuredSum,
    productId,
  };
  if (isDefined(formProps) && isDefined(isAdvisor)) {
    return {
      ...addProduct,
      oneTimePayment: formProps.disabilityCheckedToPurchase
        ? mapOneTimePayment(formProps, isAdvisor)
        : null,
    };
  }
  return addProduct;
};

const mapOneTimePayment = (
  formProps: AdvicePriceFormValues,
  isAdvisor: boolean,
): number | null => {
  const disabilityOneTimePaymentValue =
    isDefined(formProps.disabilityOneTimePaymentValue) &&
    formProps.disabilityOneTimePaymentValue > 0
      ? formProps.disabilityOneTimePaymentValue
      : null;
  if (!isAdvisor && formProps.disabilityOneTimePayment) {
    return disabilityOneTimePaymentValue;
  }
  switch (formProps.disabilityOneTimePaymentAdvisorChoice) {
    case OneTimePaymentAdvisorChoice.ADVICED_ONE_TIME_PAYMENT:
      return disabilityOneTimePaymentValue;
    case OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT:
      return formatNumberStringToNumber(
        formProps.disabilityOneTimePaymentAdvisorValue,
      );
    case OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT:
    default:
      return null;
  }
};

const getAdviceProductIdIfExists = (
  insuranceName: INSURANCE_NAME_VALUE,
  adviceProductsOnCart: AdviceProduct[],
): string | undefined =>
  adviceProductsOnCart.find(
    (product) => product.insuranceName === insuranceName,
  )?.productId;
